/**
 * createAccountSetupRunners — the ONE implementation of the §B account-setup side effects.
 *
 * # Why this exists
 *
 * apps/admin and apps/portal each carried their own copy of this file. They were not variants: both hit
 * `/api/me/v1/me/account-setup` and the SAME nine endpoints, dispatched the same actions, and parsed the
 * same schema. 105 lines differed and almost all of it was prose. The only behavioural difference in the
 * whole pair was that the portal wired an optional `signOut` and the admin did not.
 *
 * The admin copy's header explained itself: "Same shape as the portal variant; lives here because the
 * admin app's apiFetch + auth model is its own." That reason is real and it justifies about four lines —
 * the apiFetch import — not a second copy of the file. So apiFetch is now a parameter, and the file is
 * one.
 *
 * # Why this is worth doing rather than tolerating
 *
 * On 2026-07-16 every admin enrolment broke because a wire enum had two copies and only one was updated.
 * This is the same shape one level up, and it had already started: the Floor S6 change had to be written
 * into both of these files by hand, and the drift between them (a `signOut` here, a comment there) is
 * what a copy looks like before it becomes a bug. Duplicated code does not announce the moment it starts
 * lying — it typechecks perfectly in both places, forever, however far apart they drift.
 *
 * # Framework-agnostic, deliberately
 *
 * auth-core takes no React dependency (see CLAUDE.md), so the codes-ack signal arrives as `getCodesAck`,
 * a plain getter, rather than the `MutableRefObject` both copies used. Callers holding a ref pass
 * `() => ref.current`. The ref semantics that mattered — read at CALL time, not at construction — survive
 * intact, because a getter has exactly the same property. The apps keep their own `useMemo` wrappers;
 * only the effects live here.
 */

import { runAttestation, type AttestationOptionsWire } from "../webauthn-flow";
import { accountSetupStatusSchema } from "./schemas";
import type { PasskeyHint, SetupAction } from "./machine";
import type {
  BeginTOTPResponse,
  CompleteResponse,
  GenerateRecoveryCodesResponse,
} from "./types";

/**
 * The §B account-setup surface. One method per backend operation, each performing ONE side effect and
 * dispatching the matching action.
 *
 * This interface used to live in @pureid/ui next to the component that consumes it. It is pure async
 * functions with no React in it, and putting it here lets the shared runner live beside it rather
 * than in two apps. @pureid/ui re-exports it, so existing imports are unaffected.
 */
export interface AccountSetupRunners {
  /** GET /me/account-setup/status — refresh after each step. */
  refreshStatus: () => Promise<void>;
  /** POST /me/account-setup/recovery-email + dispatch send-ok/error */
  sendRecoveryEmail: (email: string) => Promise<void>;
  /** POST /me/account-setup/recovery-email/verify + dispatch verify-ok/error + refreshStatus */
  verifyRecoveryEmail: (code: string) => Promise<void>;
  /** POST /me/account-setup/recovery-codes/generate + dispatch codes-revealed */
  generateRecoveryCodes: () => Promise<void>;
  /**
   * registerPasskey runs the WebAuthn ceremony for the chosen authenticator and dispatches the OUTCOME,
   * including the tier the server inferred. The tier is not decoration: the ceremony can succeed and
   * still leave the user below the floor (a phone passkey that does not sync, a platform authenticator),
   * and she has to be told that in the same breath as "it worked".
   */
  registerPasskey: (hint: PasskeyHint) => Promise<void>;
  /** POST /me/account-setup/totp/begin + dispatch totp-begun */
  beginTOTP: () => Promise<void>;
  /** POST /me/account-setup/totp/verify + dispatch verify-ok/error + refreshStatus */
  verifyTOTP: (code: string) => Promise<void>;
  /** POST /me/account-setup/complete + dispatch complete-ok/error */
  complete: () => Promise<void>;
  /**
   * Sign-out runner. Optional — when wired, the lockout state (policy can't be satisfied by available
   * methods) renders a "Sign out and try later" button so stranded users have a one-click escape instead
   * of having to remember "close the tab to sign out." The button still respects the §B invariant: the
   * user IS signed out before dashboard access; they're not bypassing the policy.
   */
  signOut?: () => Promise<void>;
}

/** The SPA's view of whether the codes-reveal hard gate fired. Observational; see `complete`. */
export interface CodesAckTracker {
  shown: boolean;
  acknowledged: boolean;
}

/**
 * The narrow slice of an app's apiFetch that these runners use.
 *
 * Structural on purpose. Both apps' apiFetch is `<T>(path, options) => Promise<T>` with a wider options
 * bag (the admin's carries tenantSlug, for instance); each satisfies this without adaptation, and this
 * type states exactly what the runners are entitled to rely on. Widening it to either app's concrete
 * options type would re-couple auth-core to one of them, which is the thing being undone here.
 */
export type SetupApiFetch = <T>(
  path: string,
  options?: { method?: string; body?: unknown },
) => Promise<T>;

export interface AccountSetupRunnerDeps {
  apiFetch: SetupApiFetch;
  dispatch: (a: SetupAction) => void;
  /**
   * Reads the SPA's codes-acknowledgment state at CALL time. A getter rather than a value: `complete`
   * must observe what the tracker says when it runs, not what it said when the runners were built.
   * Callers holding a React ref pass `() => ref.current`.
   */
  getCodesAck?: () => CodesAckTracker | undefined;
  signOut?: () => Promise<void>;
  /** Defaults to the canonical tree. Injectable so a test can point at a fake. */
  base?: string;
}

const DEFAULT_BASE = "/api/me/v1/me/account-setup";

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  return fallback;
}

export function createAccountSetupRunners({
  apiFetch,
  dispatch,
  getCodesAck,
  signOut,
  base = DEFAULT_BASE,
}: AccountSetupRunnerDeps): AccountSetupRunners {
  // PARSE, don't cast. `verdict` decides whether a blocked user sees the method menu or an escalation
  // screen; a server that stops sending it must fail loudly rather than silently dropping every trapped
  // user back into the futile-menu loop. Hoisted because all three call sites had their own copy of this
  // three-line sequence AND its comment.
  async function loadStatus() {
    const raw = await apiFetch<unknown>(`${base}/status`);
    dispatch({ type: "status-refreshed", status: accountSetupStatusSchema.parse(raw) });
  }

  return {
    async refreshStatus() {
      try {
        await loadStatus();
      } catch (err) {
        dispatch({
          type: "status-error",
          message: errMessage(err, "Could not refresh setup status."),
        });
      }
    },

    /**
     * The D3 route. Runs the WebAuthn ceremony against the SETUP-SCOPED endpoints
     * (/me/account-setup/passkey/*), not /api/ceremony/v1/registration/*, because the user is holding a
     * scope-narrowed setup session.
     *
     * It dispatches the TIER the server inferred, not just success. That is the point of the whole slice:
     * the ceremony can succeed and leave her exactly as stuck as before, and telling her "done!" in that
     * case is the futile-option trap with a green checkmark on it. The server reads the tier back off the
     * stored credential — the same value the floor will judge her by — so what she is told and what she
     * is measured against cannot diverge.
     *
     * A trapped ADMIN is the worst case on the platform, which is why the admin's copy of this carried
     * the longest comment: every escalation this product offers ends in "contact your administrator",
     * which is a dead end when you ARE the administrator. That is an argument for this code being
     * identical in both apps, not for it being written twice.
     */
    async registerPasskey(hint: PasskeyHint) {
      // Scoped to this ONE call, not a ref: the ceremony id is meaningful only between this begin and its
      // own finish. A ref would outlive the call and let a second ceremony finish against the first's id.
      let ceremonyId = "";
      try {
        const res = await runAttestation(
          // The setup reducer owns this flow's phases; runAttestation's own ceremony machine is not
          // rendered here, so its dispatch is a no-op rather than a second source of UI state.
          () => {},
          async () => {
            const raw = await apiFetch<{
              ceremony_id: string;
              options: AttestationOptionsWire;
            }>(`${base}/passkey/begin`, { method: "POST", body: { hint } });
            ceremonyId = raw.ceremony_id;
            return raw.options;
          },
          async (signed) =>
            apiFetch<{
              backing_tier: "D3" | "D2" | "D1";
              friendly_name?: string;
              complete: boolean;
            }>(`${base}/passkey/finish`, {
              method: "POST",
              body: { ceremony_id: ceremonyId, response: signed },
            }),
        );
        dispatch({
          type: "passkey-ok",
          tier: res.backing_tier,
          friendlyName: res.friendly_name ?? "",
          complete: res.complete,
        });
      } catch (err) {
        dispatch({
          type: "passkey-error",
          message: errMessage(err, "Could not add that passkey."),
        });
      }
    },

    async sendRecoveryEmail(email: string) {
      try {
        await apiFetch<void>(`${base}/recovery-email`, { method: "POST", body: { email } });
        dispatch({ type: "email-send-ok", email });
      } catch (err) {
        dispatch({ type: "email-send-error", message: errMessage(err, "Could not send code.") });
      }
    },

    async verifyRecoveryEmail(code: string) {
      try {
        await apiFetch<void>(`${base}/recovery-email/verify`, { method: "POST", body: { code } });
        dispatch({ type: "email-verify-ok" });
        // Re-fetch so the menu reflects the new verified state; the reducer's status-refreshed case
        // routes back to the menu.
        await loadStatus();
      } catch (err) {
        dispatch({
          type: "email-verify-error",
          message: errMessage(err, "That code didn't match. Request a fresh one and try again."),
        });
      }
    },

    async generateRecoveryCodes() {
      try {
        const res = await apiFetch<GenerateRecoveryCodesResponse>(
          `${base}/recovery-codes/generate`,
          { method: "POST" },
        );
        dispatch({ type: "codes-revealed", codes: res.codes, setId: res.set_id });
      } catch (err) {
        dispatch({
          type: "status-error",
          message: errMessage(err, "Could not generate recovery codes."),
        });
      }
    },

    async beginTOTP() {
      try {
        const res = await apiFetch<BeginTOTPResponse>(`${base}/totp/begin`, { method: "POST" });
        dispatch({ type: "totp-begun", enrollment: res });
      } catch (err) {
        dispatch({
          type: "status-error",
          message: errMessage(err, "Could not start authenticator setup."),
        });
      }
    },

    async verifyTOTP(code: string) {
      dispatch({ type: "totp-verify-start" });
      try {
        await apiFetch<void>(`${base}/totp/verify`, { method: "POST", body: { code } });
        dispatch({ type: "totp-verify-ok" });
        await loadStatus();
      } catch (err) {
        dispatch({
          type: "totp-verify-error",
          message: errMessage(
            err,
            "That code didn't match. Try the most recent code from your authenticator app.",
          ),
        });
      }
    },

    signOut: signOut
      ? async () => {
          try {
            await signOut();
          } catch {
            /* sign-out failures are non-fatal — the user's view tears down regardless via the caller's
               cache invalidation. */
          }
        }
      : undefined,

    async complete() {
      // Audit signal: pass through the SPA's view of whether the codes-reveal hard gate fired in
      // this session. Observational only — the server does not reject based on it — but it lands on the
      // account_setup.completed audit row for SOC pattern detection.
      const ack = getCodesAck?.();
      const body =
        ack && (ack.shown || ack.acknowledged)
          ? {
              codes_acknowledgment: {
                codes_shown: ack.shown,
                codes_acknowledged: ack.acknowledged,
              },
            }
          : undefined;
      try {
        const res = await apiFetch<CompleteResponse>(`${base}/complete`, { method: "POST", body });
        dispatch({
          type: "complete-ok",
          token: res.token,
          sessionId: res.session_id,
          expiresAt: res.expires_at,
          successorType: res.successor_type,
        });
      } catch (err) {
        dispatch({
          type: "complete-error",
          message: errMessage(
            err,
            "Could not finish setting up your account. Sign in again to retry.",
          ),
        });
      }
    },
  };
}
